/**
 * O Agente Silva no painel do Logigate: o botão flutuante (a cara do Silva), a bolha de
 * boas-vindas e o painel da conversa, em Tailwind (o kit `@/Logigate/ui`), sem o Mantine.
 *
 * O painel não é uma janela modal: fica ao lado da página, que continua a poder ser usada
 * enquanto se fala com o Silva. Fecha no X, no botão flutuante e com Esc (quando não há uma
 * janela do kit nem um aviso abertos por cima). Como o botão, leva `data-flutuante-painel`:
 * some enquanto há uma janela ou gaveta do kit aberta (o CSS em `app.css`) sem perder a conversa.
 */
import { useState, useRef, useEffect, KeyboardEvent, lazy, Suspense } from 'react';
import { ArrowLeft, Ban, Check, CircleAlert, Clock, ExternalLink, History, MessageSquare, Play, Plus, SendHorizontal, Trash2, X } from 'lucide-react';
import { Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { track } from '@/utils/px';
import { cn } from '@/lib/utils';
import { Botao, BotaoIcone, Entrada, Spinner, useSaida } from '@/Logigate/ui';
import { useAgenteSilva, AgenteSilvaProvider } from './AgenteSilvaContext';
import { AgenteSilvaWidgetProps, Mensagem, Conversa, AccaoSilva, VideoSilva, ArtigoSilva } from './types';

// O leitor da central de ajuda (com o hls.js) só se carrega quando alguém abre um vídeo.
const ReprodutorVideo = lazy(() => import('@/Ajuda/componentes/artigo/ReprodutorVideo').then(m => ({ default: m.ReprodutorVideo })));

const AVATAR_SILVA = '/_ajuda/imagens/avatar_agente_silva.avif';

/** Os botões do cabeçalho do painel: 40 px no telemóvel (alvo de toque), 32 px no computador. */
const BOTAO_CABECALHO = 'size-10 md:size-8';

/** Como fica o cartão depois de decidido. */
const DEPOIS_DE_DECIDIDA: Partial<Record<AccaoSilva['estado'], { texto: string; classes: string; Icone: typeof Check }>> = {
    confirmada: { texto: 'Confirmado', classes: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300', Icone: Check },
    cancelada: { texto: 'Cancelado', classes: 'bg-zinc-200/70 text-zinc-700 dark:bg-zinc-700/60 dark:text-zinc-200', Icone: Ban },
    expirada: { texto: 'Já não está à espera: pede de novo ao Silva', classes: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300', Icone: Clock },
    recusada: { texto: 'Não foi possível confirmar', classes: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300', Icone: CircleAlert },
    falhou: { texto: 'Não foi concluído', classes: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300', Icone: CircleAlert },
};

/**
 * Os botões de uma acção que o Silva preparou (lembrete de pagamento, proforma). O resumo
 * exacto está no texto da mensagem, por cima; o cartão só pede ao servidor que execute ou
 * cancele — é ele que verifica e decide. Escrever «sim» ou «não» na conversa faz o mesmo.
 */
function CartaoDaAccao({ mensagem }: { mensagem: Mensagem }) {
    const { decidirAccao, aDecidir } = useAgenteSilva();
    const accao = mensagem.accao;
    const [agora, setAgora] = useState(() => Date.now());

    // O pedido vale 15 minutos: o cartão passa a «expirado» sozinho, sem esperar pelo clique.
    useEffect(() => {
        if (accao?.estado !== 'pendente') {
            return;
        }
        const t = window.setInterval(() => setAgora(Date.now()), 30000);
        return () => window.clearInterval(t);
    }, [accao?.estado]);

    if (!accao || accao.estado === 'descartada') {
        return null;
    }

    const expirou = accao.estado === 'pendente' && !!accao.expira_em && new Date(accao.expira_em).getTime() <= agora;
    const estado: AccaoSilva['estado'] = expirou ? 'expirada' : accao.estado;

    if (estado === 'pendente') {
        const aConfirmar = aDecidir?.accaoId === accao.id && aDecidir.confirmar;
        const aCancelar = aDecidir?.accaoId === accao.id && !aDecidir.confirmar;
        const hora = accao.expira_em
            ? new Date(accao.expira_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
            : null;

        return (
            <div
                data-accao-silva={accao.id}
                className="mt-3 rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
            >
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {accao.titulo ?? 'Pedido'}
                    {hora && <> · vale até às {hora}</>}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                    <Botao
                        tamanho="sm"
                        icone={<Check className="size-4" />}
                        aCarregar={aConfirmar}
                        disabled={!!aDecidir && !aConfirmar}
                        onClick={() => { track('click', 'agente_silva_confirmar_accao', { md: { tipo: accao.tipo } }); decidirAccao(mensagem.id, accao, true); }}
                    >
                        Confirmar
                    </Botao>
                    <Botao
                        tamanho="sm"
                        variante="neutro"
                        icone={<X className="size-4" />}
                        aCarregar={aCancelar}
                        disabled={!!aDecidir && !aCancelar}
                        onClick={() => { track('click', 'agente_silva_cancelar_accao', { md: { tipo: accao.tipo } }); decidirAccao(mensagem.id, accao, false); }}
                    >
                        Cancelar
                    </Botao>
                </div>
            </div>
        );
    }

    const fim = DEPOIS_DE_DECIDIDA[estado];

    if (!fim) {
        return null;
    }

    return (
        <p className={cn('mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', fim.classes)}>
            <fim.Icone className="size-3.5" aria-hidden />
            {fim.texto}
        </p>
    );
}

/**
 * Os vídeos de ajuda de uma resposta (os dos artigos que ela usou): a capa, o título e a duração.
 * Ao clicar abre-se ali o leitor da central de ajuda.
 */
function VideosDaResposta({ videos }: { videos: VideoSilva[] }) {
    const [aberto, setAberto] = useState<string | null>(null);

    return (
        <div className="mt-3 space-y-2">
            {videos.map(v => (
                <div key={v.chave} className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                    {aberto === v.chave ? (
                        <Suspense fallback={<div className="flex aspect-video items-center justify-center"><Spinner /></div>}>
                            <ReprodutorVideo fonte={v.url_atual} titulo={v.titulo} />
                        </Suspense>
                    ) : (
                        <button
                            type="button"
                            onClick={() => { track('click', 'agente_silva_ver_video', { md: { chave: v.chave } }); setAberto(v.chave); }}
                            className="group flex w-full items-center gap-3 p-2 text-left"
                        >
                            <span className="relative flex aspect-video w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-200 dark:bg-zinc-800">
                                {v.url_capa && <img src={v.url_capa} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />}
                                <span className="relative flex size-8 items-center justify-center rounded-full bg-black/60 text-white transition-colors group-hover:bg-black/75">
                                    <Play className="size-4" />
                                </span>
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">{v.titulo}</span>
                                <span className="block text-xs text-zinc-500 dark:text-zinc-400">Vídeo de ajuda{v.duracao_legivel ? ` · ${v.duracao_legivel}` : ''}</span>
                            </span>
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

/** A referência da resposta: os artigos da central de ajuda de onde vêm os passos. */
function ArtigosDaResposta({ artigos }: { artigos: ArtigoSilva[] }) {
    return (
        <div className="mt-3 border-t border-zinc-200 pt-2 dark:border-zinc-700">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Na central de ajuda</p>
            <ul className="mt-1 space-y-1">
                {artigos.map(a => (
                    <li key={a.id}>
                        <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => track('click', 'agente_silva_abrir_artigo', { md: { id: a.id } })}
                            className="inline-flex items-center gap-1 text-sm font-medium text-portal hover:underline"
                        >
                            {a.titulo}
                            <ExternalLink className="size-3.5 shrink-0" />
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Uma mensagem da conversa: as do utilizador à direita, na cor do portal; as do Silva em Markdown. */
function BolhaMensagem({ mensagem }: { mensagem: Mensagem }) {
    const doUtilizador = mensagem.tipo === 'utilizador';

    return (
        <div className={cn('flex', doUtilizador ? 'justify-end' : 'justify-start')}>
            <div
                className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-2.5',
                    doUtilizador
                        ? 'rounded-br-md bg-portal text-white'
                        : 'rounded-bl-md bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100',
                )}
            >
                {doUtilizador ? (
                    <p className="whitespace-pre-wrap break-words text-sm">{mensagem.texto}</p>
                ) : (
                    <div className="prose prose-sm max-w-none overflow-x-auto dark:prose-invert prose-headings:mb-2 prose-headings:mt-3 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-3 prose-table:my-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{mensagem.texto}</ReactMarkdown>

                        {/* Enquanto o texto ainda está a chegar, um cursor a piscar. */}
                        {mensagem.aEscrever && (
                            <span className="-mb-0.5 ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-zinc-500 dark:bg-zinc-300" aria-hidden />
                        )}
                    </div>
                )}

                {/* Os vídeos de ajuda dos artigos que a resposta usou. */}
                {!doUtilizador && !mensagem.aEscrever && !!mensagem.videos?.length && <VideosDaResposta videos={mensagem.videos} />}

                {/* De onde vêm os passos: os artigos da central de ajuda. */}
                {!doUtilizador && !mensagem.aEscrever && !!mensagem.artigos?.length && <ArtigosDaResposta artigos={mensagem.artigos} />}

                {/* Uma acção à espera de confirmação: os botões, por baixo do resumo. */}
                {!doUtilizador && !mensagem.aEscrever && mensagem.accao && <CartaoDaAccao mensagem={mensagem} />}

                {/* O agente demora quando consulta a base: dizer o que está a fazer vale mais do
                    que uma animação genérica. */}
                {mensagem.aConsultar && (
                    <p className="mt-1 text-xs italic text-zinc-500 dark:text-zinc-400">{mensagem.aConsultar}…</p>
                )}

                {!mensagem.aEscrever && (
                    <p className={cn('mt-1 text-[11px]', doUtilizador ? 'text-white/75' : 'text-zinc-500 dark:text-zinc-400')}>
                        {new Date(mensagem.created_at).toLocaleTimeString('pt-PT', {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                )}
            </div>
        </div>
    );
}

/** Os três pontos do «a escrever», antes de chegar a primeira letra da resposta. */
function IndicadorAEscrever() {
    return (
        <div className="flex justify-start" role="status" aria-label="O Agente Silva está a escrever">
            <div className="rounded-2xl rounded-bl-md bg-zinc-100 px-4 py-3 dark:bg-zinc-800">
                <div className="flex gap-1">
                    <span className="size-2 animate-bounce rounded-full bg-zinc-400 dark:bg-zinc-500" style={{ animationDelay: '0ms' }} />
                    <span className="size-2 animate-bounce rounded-full bg-zinc-400 dark:bg-zinc-500" style={{ animationDelay: '150ms' }} />
                    <span className="size-2 animate-bounce rounded-full bg-zinc-400 dark:bg-zinc-500" style={{ animationDelay: '300ms' }} />
                </div>
            </div>
        </div>
    );
}

/** Uma conversa antiga, na lista do histórico. */
function ItemConversa({ conversa, onClick }: { conversa: Conversa; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-start justify-between gap-3 rounded-xl border border-zinc-200 bg-white p-3 text-left transition-colors hover:border-zinc-300 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700 dark:hover:bg-zinc-800"
        >
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">Conversa #{conversa.id}</span>
                <span className="block text-xs text-zinc-500 dark:text-zinc-400">{conversa.mensagens_count || 0} mensagens</span>
            </span>
            <span className="shrink-0 text-xs text-zinc-500 dark:text-zinc-400">
                {new Date(conversa.created_at).toLocaleDateString('pt-PT', {
                    day: '2-digit',
                    month: '2-digit',
                })}
            </span>
        </button>
    );
}

/** A lista das conversas anteriores (no lugar da conversa, dentro do painel). */
function VistaHistorico() {
    const { conversas, isLoading, selecionarConversa, toggleHistoryView } = useAgenteSilva();

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b border-zinc-100 px-3 py-2 dark:border-zinc-800">
                <BotaoIcone
                    rotulo="Voltar à conversa"
                    className={BOTAO_CABECALHO}
                    onClick={() => { track('click', 'agente_silva_voltar_do_historico', {}); toggleHistoryView(); }}
                >
                    <ArrowLeft className="size-4" />
                </BotaoIcone>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">Histórico de conversas</h3>
            </div>

            <div className="scroll-bonito min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <Spinner />
                    </div>
                ) : conversas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-zinc-500 dark:text-zinc-400">
                        <History className="size-10 opacity-50" aria-hidden />
                        <p className="text-sm">Sem conversas anteriores</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {conversas.map((conversa) => (
                            <ItemConversa
                                key={conversa.id}
                                conversa={conversa}
                                onClick={() => { track('click', 'agente_silva_selecionar_conversa', { md: { id: conversa.id } }); selecionarConversa(conversa); }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/** A conversa: as mensagens (com scroll próprio) e o campo para escrever, preso em baixo. */
function VistaConversa() {
    const { mensagens, isLoading, enviarMensagem, creditos, paginaCreditos } = useAgenteSilva();
    const [texto, setTexto] = useState('');
    const rolo = useRef<HTMLDivElement>(null);
    const campo = useRef<HTMLInputElement>(null);

    // Desce até à última mensagem quando chega uma nova (ou mais texto dela).
    useEffect(() => {
        const t = window.setTimeout(() => {
            rolo.current?.scrollTo({ top: rolo.current.scrollHeight, behavior: 'smooth' });
        }, 50);
        return () => window.clearTimeout(t);
    }, [mensagens, isLoading]);

    // O foco vai para o campo ao abrir (e ao voltar do histórico).
    useEffect(() => {
        campo.current?.focus({ preventScroll: true });
    }, []);

    // Depois de o Silva responder, o campo volta a aceitar texto: o foco volta para lá.
    useEffect(() => {
        if (!isLoading) {
            campo.current?.focus({ preventScroll: true });
        }
    }, [isLoading]);

    const enviar = async () => {
        if (!texto.trim() || isLoading) {
            return;
        }
        const mensagem = texto;
        setTexto('');
        await enviarMensagem(mensagem);
    };

    const aoPremirTecla = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            enviar();
        }
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div ref={rolo} className="scroll-bonito min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
                <div className="space-y-3 p-4 pb-6">
                    {mensagens.length === 0 && !isLoading && (
                        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center text-zinc-500 dark:text-zinc-400">
                            <MessageSquare className="size-10 opacity-50" aria-hidden />
                            <p className="text-sm">Começa uma conversa com o Agente Silva</p>
                        </div>
                    )}

                    {mensagens.length === 0 && isLoading && (
                        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center text-zinc-500 dark:text-zinc-400">
                            <Spinner tamanho="lg" />
                            <p className="text-sm">A processar…</p>
                        </div>
                    )}

                    {mensagens.map((mensagem) => (
                        <BolhaMensagem key={mensagem.id} mensagem={mensagem} />
                    ))}

                    {isLoading && !mensagens.some((m) => m.aEscrever) && <IndicadorAEscrever />}
                </div>
            </div>

            <div className="shrink-0 border-t border-zinc-100 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                <div className="flex items-center gap-2">
                    <Entrada
                        ref={campo}
                        aria-label="Mensagem para o Agente Silva"
                        placeholder="Escreve a tua mensagem…"
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={aoPremirTecla}
                        disabled={isLoading}
                        autoComplete="off"
                        classNameCampo="min-w-0 flex-1"
                        className="h-11 rounded-full px-4 md:h-10"
                    />
                    <BotaoIcone
                        rotulo="Enviar"
                        variante="cheio"
                        cor="marca"
                        aCarregar={isLoading}
                        disabled={!texto.trim()}
                        className="size-11 rounded-full md:size-10"
                        onClick={() => { track('click', 'agente_silva_enviar_mensagem', {}); enviar(); }}
                    >
                        <SendHorizontal className="size-[18px]" />
                    </BotaoIcone>
                </div>

                {creditos && (
                    <p className="mt-2 px-1 text-center text-[11px] text-zinc-500 dark:text-zinc-400" data-custo-ia>
                        {creditos.saldo >= creditos.custo ? (
                            <>
                                Cada mensagem gasta {creditos.custo} {creditos.custo === 1 ? 'crédito' : 'créditos'} de IA · tens{' '}
                                <span className="font-medium tabular-nums text-zinc-700 dark:text-zinc-200">{creditos.saldo.toLocaleString('pt-PT')}</span>
                            </>
                        ) : (
                            <>
                                Os créditos de IA não chegam para mais mensagens.{' '}
                                <Link href={paginaCreditos} className="font-medium text-red-600 underline dark:text-red-400">
                                    Comprar créditos
                                </Link>
                            </>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}

/** O painel do chat, por cima do botão flutuante. */
function PainelConversa({ aSair }: { aSair: boolean }) {
    const { isHistoryView, limparConversa, iniciarNovaConversa, fecharChat, toggleHistoryView } = useAgenteSilva();

    return (
        <div
            data-flutuante-painel
            role="dialog"
            aria-label="Agente Silva"
            aria-hidden={aSair || undefined}
            className={cn(
                'fixed right-2 z-2147483647 flex w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl shadow-zinc-950/20 sm:right-6 sm:w-[500px] dark:border-zinc-800 dark:bg-zinc-900',
                // No telemóvel fica acima do botão (que está acima da barra de baixo do casco).
                'bottom-[calc(8.5rem+env(safe-area-inset-bottom))] h-[calc(100dvh-9.5rem-env(safe-area-inset-bottom))] md:bottom-20 md:h-[calc(100dvh-6.5rem)]',
                'max-h-[800px] min-h-[320px]',
                aSair
                    ? 'pointer-events-none animate-out fade-out-0 slide-out-to-bottom-4 fill-mode-forwards duration-150'
                    : 'animate-in fade-in-0 slide-in-from-bottom-4 duration-200',
            )}
        >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-100 py-2 pl-4 pr-2 dark:border-zinc-800">
                <div className="flex min-w-0 items-center gap-3">
                    <span className="relative shrink-0">
                        <img src={AVATAR_SILVA} alt="" className="size-9 rounded-full object-cover" />
                        <span className="absolute bottom-0 right-0 size-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" aria-hidden />
                    </span>
                    <div className="min-w-0 leading-tight">
                        <h2 className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-50">Agente Silva</h2>
                        <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">Assistente virtual</p>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    <BotaoIcone
                        rotulo="Histórico"
                        className={BOTAO_CABECALHO}
                        onClick={() => { track('click', 'agente_silva_abrir_historico', {}); toggleHistoryView(); }}
                    >
                        <History className="size-4" />
                    </BotaoIcone>
                    <BotaoIcone
                        rotulo="Nova conversa"
                        className={BOTAO_CABECALHO}
                        onClick={() => { track('click', 'agente_silva_nova_conversa', {}); iniciarNovaConversa(); }}
                    >
                        <Plus className="size-4" />
                    </BotaoIcone>
                    {!isHistoryView && (
                        <BotaoIcone
                            rotulo="Limpar conversa"
                            className={BOTAO_CABECALHO}
                            onClick={() => { track('click', 'agente_silva_limpar_conversa', {}); limparConversa(); }}
                        >
                            <Trash2 className="size-4" />
                        </BotaoIcone>
                    )}
                    <BotaoIcone
                        rotulo="Fechar"
                        className={BOTAO_CABECALHO}
                        onClick={() => { track('click', 'agente_silva_fechar_chat', {}); fecharChat(); }}
                    >
                        <X className="size-4" />
                    </BotaoIcone>
                </div>
            </div>

            {isHistoryView ? <VistaHistorico /> : <VistaConversa />}
        </div>
    );
}

// Session storage key and duration (2 hours in milliseconds)
const WELCOME_MESSAGE_KEY = 'agente_silva_welcome_shown';
const WELCOME_MESSAGE_DURATION = 2 * 60 * 60 * 1000; // 2 hours

// Floating button component with welcome message
function FloatingButton() {
    const { isOpen, toggleChat, config } = useAgenteSilva();
    const [showWelcome, setShowWelcome] = useState(false);

    // Check if we should show the welcome message
    useEffect(() => {
        if (isOpen) {
            setShowWelcome(false);
            return;
        }

        const checkWelcomeMessage = () => {
            try {
                const lastShown = localStorage.getItem(WELCOME_MESSAGE_KEY);
                const now = Date.now();

                if (!lastShown) {
                    // Never shown before, show it after a small delay
                    setTimeout(() => setShowWelcome(true), 1000);
                    return;
                }

                const lastShownTime = parseInt(lastShown, 10);
                const timePassed = now - lastShownTime;

                // Show again if 2 hours have passed
                if (timePassed >= WELCOME_MESSAGE_DURATION) {
                    setTimeout(() => setShowWelcome(true), 1000);
                }
            } catch (e) {
                // localStorage not available, show the message
                setTimeout(() => setShowWelcome(true), 1000);
            }
        };

        checkWelcomeMessage();
    }, [isOpen]);

    // Handle dismissing the welcome message
    const dismissWelcome = () => {
        setShowWelcome(false);
        try {
            localStorage.setItem(WELCOME_MESSAGE_KEY, Date.now().toString());
        } catch (e) {
            // localStorage not available
        }
    };

    // Handle clicking the chat button
    const handleClick = () => {
        if (showWelcome) {
            dismissWelcome();
        }
        toggleChat();
    };

    // Don't render if chat is disabled
    if (config && !config.enabled) {
        return null;
    }

    return (
        // No telemóvel o painel tem a barra de navegação em baixo: o botão fica por cima dela.
        <div data-flutuante-painel className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-3 z-2147483646 md:bottom-5 md:right-5">
            {/* A bolha de boas-vindas, por cima do botão */}
            {/* `data-boas-vindas-silva`: some numa página com uma barra presa em baixo (o CSS em
                app.css), onde tapava o botão «Guardar». */}
            {showWelcome && !isOpen && (
                <div data-boas-vindas-silva className="absolute bottom-[68px] right-0 origin-bottom-right animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-300">
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => { track('click', 'agente_silva_abrir_chat_via_welcome', {}); handleClick(); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
                        className="flex w-[260px] max-w-[calc(100vw-2rem)] cursor-pointer items-center gap-2.5 rounded-2xl border border-zinc-200 bg-white py-2 pl-2 pr-2 shadow-lg shadow-zinc-950/10 transition-shadow hover:shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <img src={AVATAR_SILVA} alt="" className="size-9 shrink-0 rounded-full object-cover" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-zinc-900 dark:text-white">Agente Silva</span>
                            <span className="block text-xs text-zinc-500 dark:text-zinc-400">Olá! Precisas de ajuda?</span>
                        </span>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                track('click', 'agente_silva_fechar_welcome', {});
                                dismissWelcome();
                            }}
                            className="flex size-7 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                            aria-label="Fechar"
                        >
                            <X className="size-3.5" />
                        </button>
                    </div>
                    <span className="absolute -bottom-1.5 right-[19px] size-3 rotate-45 border-b border-r border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900" aria-hidden />
                </div>
            )}

            {/* O botão: a cara do Silva com o ponto de «disponível»; aberto, um X. */}
            <button
                type="button"
                onClick={() => { track('click', isOpen ? 'agente_silva_fechar_float' : 'agente_silva_abrir_float', {}); handleClick(); }}
                aria-label={isOpen ? 'Fechar o chat do Agente Silva' : 'Falar com o Agente Silva'}
                aria-expanded={isOpen}
                title={isOpen ? 'Fechar o chat' : 'Falar com o Agente Silva'}
                className="relative flex size-12 items-center justify-center rounded-full bg-white shadow-[0_10px_24px_-10px_rgb(15_23_42/0.45),0_2px_6px_rgb(15_23_42/0.12)] ring-2 ring-white transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-portal/50 md:size-[52px] dark:bg-zinc-900 dark:ring-zinc-900"
            >
                {isOpen ? (
                    <span className="flex size-full items-center justify-center rounded-full bg-portal text-white dark:bg-portal-claro dark:text-zinc-950">
                        <X className="size-[22px]" />
                    </span>
                ) : (
                    <>
                        <img src={AVATAR_SILVA} alt="" className="size-full rounded-full object-cover" />
                        <span className="absolute bottom-0.5 right-0.5 size-3 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" aria-hidden />
                    </>
                )}
            </button>
        </div>
    );
}

// Main widget component (with provider)
function AgenteSilvaWidgetInner() {
    const { isOpen, config, fecharChat } = useAgenteSilva();
    const { montada, aSair } = useSaida(isOpen, 150);

    // Esc fecha o painel — mas não quando há por cima uma janela/gaveta do kit (que fecha ela)
    // ou um aviso do SweetAlert.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const tecla = (e: globalThis.KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) {
                return;
            }
            if (document.body.classList.contains('swal2-shown') || document.querySelector('[data-sobreposicao-logigate]')) {
                return;
            }
            fecharChat();
        };
        document.addEventListener('keydown', tecla);
        return () => document.removeEventListener('keydown', tecla);
    }, [isOpen, fecharChat]);

    // Don't render anything if chat is disabled
    if (config && !config.enabled) {
        return null;
    }

    return (
        <>
            <FloatingButton />
            {montada && <PainelConversa aSair={aSair} />}
        </>
    );
}

// Export wrapped component
export function AgenteSilvaWidget(props: AgenteSilvaWidgetProps) {
    return (
        <AgenteSilvaProvider {...props}>
            <AgenteSilvaWidgetInner />
        </AgenteSilvaProvider>
    );
}

export default AgenteSilvaWidget;
